import { AlertTriangle, ArrowRight, Bitcoin, CheckCircle2, Copy, Loader2, RefreshCcw, Sparkles } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { createCryptoDeposit, estimateCryptoDeposit, listMyCryptoDeposits } from '@/api/cryptoDeposit';

/**
 * CryptoDepositPanel — v2 single-use deposit flow.
 *
 * Two-screen modal-less panel:
 *
 *   SCREEN A — amount entry
 *     • Coin dropdown: LTC / DOGE / TRX
 *     • Crypto-amount number input (debounced live USD estimate)
 *     • Red/green minimum gate at $5.00
 *     • "Generate Deposit Address" button disabled until green
 *
 *   SCREEN B — address display
 *     • Send exactly: <pay_amount> <pay_currency>  (copy button)
 *     • To address:   <pay_address>                (copy button)
 *     • ~$X estimated (display only — actual credited amount is the
 *       USD value of whatever ACTUALLY arrives on-chain)
 *     • Status badge with 15s polling
 *     • "New Deposit" returns to Screen A
 *
 * This panel sits ALONGSIDE the existing LiveDepositPanel. The existing
 * flow remains the default; this is an additional option labeled
 * "Crypto Deposit (New)".
 */

// Coin catalog — server enforces the same whitelist.
const COINS = [
  { code: 'LTC', label: 'Litecoin', network: 'Litecoin mainnet' },
  { code: 'DOGE', label: 'Dogecoin', network: 'Dogecoin mainnet' },
  { code: 'TRX', label: 'TRON', network: 'TRON (TRC20)' },
];

// $5.00 floor in cents — kept in sync with the server constant.
const MIN_USD_CENTS = 500;

// Polling interval for deposit-status checks. Per platform self-DDoS
// guardrails we keep this >= 15s. Stops automatically once the deposit
// reaches a terminal status.
const POLL_INTERVAL_MS = 15_000;

// Debounce delay for the live estimate. Spec says ~400ms.
const ESTIMATE_DEBOUNCE_MS = 400;

const TERMINAL_STATUSES = new Set(['finished', 'partially_paid']);
const ACTIVE_STATUSES = new Set(['waiting', 'confirming', 'confirmed', 'sending']);

const formatUsd = (cents) => `$${(Number(cents || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const safeMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'object' && d) return d.message || d.code || fallback;
  if (typeof d === 'string') return d;
  return err?.message || fallback;
};

// ---------------------------------------------------------------------------
// Screen A — amount entry with the red->green gate.
// ---------------------------------------------------------------------------
const AmountEntryScreen = ({ onAddressGenerated }) => {
  const uid = useId();
  const [coin, setCoin] = useState('LTC');
  const [amount, setAmount] = useState('');
  const [estimate, setEstimate] = useState(null); // { usd_amount_cents, meets_minimum, ... }
  const [estimating, setEstimating] = useState(false);
  const [estError, setEstError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // Re-estimate when coin or amount changes (debounced).
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setEstError(null);

    const numeric = Number(amount);
    if (!amount || Number.isNaN(numeric) || numeric <= 0) {
      setEstimate(null);
      setEstimating(false);
      return;
    }

    setEstimating(true);
    const myReq = ++reqIdRef.current;
    debounceRef.current = window.setTimeout(async () => {
      try {
        const data = await estimateCryptoDeposit(coin, amount);
        if (reqIdRef.current === myReq) {
          setEstimate(data);
          setEstError(null);
        }
      } catch (err) {
        if (reqIdRef.current === myReq) {
          setEstimate(null);
          setEstError(safeMsg(err, 'Unable to fetch live rate'));
        }
      } finally {
        if (reqIdRef.current === myReq) setEstimating(false);
      }
    }, ESTIMATE_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [coin, amount]);

  // Clear amount + estimate when coin changes.
  const handleCoinChange = (newCoin) => {
    setCoin(newCoin);
    setAmount('');
    setEstimate(null);
    setEstError(null);
  };

  const meetsMin = Boolean(estimate?.meets_minimum);
  const buttonDisabled = !estimate || !meetsMin || estimating || submitting;

  const handleSubmit = async () => {
    if (buttonDisabled) return;
    setSubmitting(true);
    try {
      const resp = await createCryptoDeposit(coin, amount);
      onAddressGenerated(resp);
    } catch (err) {
      toast.error(safeMsg(err, 'Could not generate deposit address'));
    } finally {
      setSubmitting(false);
    }
  };

  const usdColor = !estimate
    ? 'var(--sc-text-mute)'
    : meetsMin
      ? '#86efac' // green
      : '#fca5a5'; // red

  return (
    <div className="space-y-5" data-testid="crypto-deposit-screen-amount">
      {/* Coin selector */}
      <div>
        <span className="block text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-2" style={{ color: 'var(--sc-text-mute)' }}>
          Cryptocurrency
        </span>
        <div className="grid grid-cols-3 gap-2">
          {COINS.map((c) => {
            const active = c.code === coin;
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => handleCoinChange(c.code)}
                data-testid={`crypto-deposit-coin-btn-${c.code.toLowerCase()}`}
                className="rounded-xl px-3 py-3 text-left transition"
                style={{
                  background: active ? 'linear-gradient(135deg, rgba(255,194,14,0.18), rgba(255,182,18,0.08))' : 'rgba(255,255,255,0.03)',
                  border: active ? '1px solid rgba(255,194,14,0.6)' : '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: active ? '#FFC20E' : 'var(--sc-text-mute)' }}>
                  {c.code}
                </div>
                <div className="font-display font-semibold text-[13px] mt-0.5" style={{ color: 'var(--sc-text)' }}>
                  {c.label}
                </div>
                <div className="text-[10px] mt-0.5" style={{ color: 'var(--sc-text-dim)' }}>
                  {c.network}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Amount input */}
      <div>
        <label
          htmlFor={`${uid}-amount`}
          className="block text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-2"
          style={{ color: 'var(--sc-text-mute)' }}
        >
          Amount in {coin}
        </label>
        <div className="relative">
          <input
            id={`${uid}-amount`}
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Enter ${coin} amount…`}
            data-testid="crypto-deposit-amount-input"
            className="w-full rounded-xl px-4 py-3 font-mono text-[15px] outline-none"
            style={{
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'var(--sc-text)',
            }}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-bold" style={{ color: 'var(--sc-text-mute)' }}>
            {coin}
          </div>
        </div>
      </div>

      {/* Live USD estimate with red->green gate */}
      <div
        className="rounded-xl p-4"
        style={{
          background: 'rgba(0,0,0,0.32)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
        data-testid="crypto-deposit-estimate-panel"
      >
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold mb-1" style={{ color: 'var(--sc-text-mute)' }}>
          Live USD value
        </div>

        {estimating ? (
          <div className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--sc-text-dim)' }} data-testid="crypto-deposit-estimating">
            <Loader2 className="w-4 h-4 animate-spin" />
            Fetching live rate from NOWPayments…
          </div>
        ) : estError ? (
          <div className="flex items-center gap-2 text-[13px]" style={{ color: '#fca5a5' }} data-testid="crypto-deposit-estimate-error">
            <AlertTriangle className="w-4 h-4" />
            {estError}
          </div>
        ) : estimate ? (
          <>
            <div className="font-display font-bold text-2xl tracking-tight" style={{ color: usdColor }} data-testid="crypto-deposit-estimate-usd">
              {formatUsd(estimate.usd_amount_cents)}
            </div>
            <div className="text-[11px] mt-1" style={{ color: meetsMin ? '#86efac' : '#fca5a5' }} data-testid="crypto-deposit-estimate-gate">
              {meetsMin
                ? 'Meets the $5.00 minimum — you can generate a deposit address.'
                : `Below the $${(MIN_USD_CENTS / 100).toFixed(2)} minimum — increase the ${coin} amount to continue.`}
            </div>
          </>
        ) : (
          <div className="text-[12px]" style={{ color: 'var(--sc-text-dim)' }}>
            Enter a {coin} amount above to see the live USD value.
          </div>
        )}
      </div>

      {/* Generate button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={buttonDisabled}
        data-testid="crypto-deposit-generate-btn"
        className="w-full rounded-xl py-3 font-semibold text-[14px] flex items-center justify-center gap-2 transition"
        style={{
          background: buttonDisabled ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #FFC20E, #FFB612)',
          color: buttonDisabled ? 'var(--sc-text-mute)' : '#0a0a0b',
          border: buttonDisabled ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,182,18,0.85)',
          cursor: buttonDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {submitting ? 'Generating address…' : 'Generate Deposit Address'}
        {!submitting && !buttonDisabled && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Screen B — show the unique address + live status polling.
// ---------------------------------------------------------------------------
const AddressDisplayScreen = ({ deposit, onReset }) => {
  const [currentStatus, setCurrentStatus] = useState(deposit.status);
  const [credited, setCredited] = useState(false);
  const [creditedCents, setCreditedCents] = useState(0);
  const [actuallyPaid, setActuallyPaid] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Poll deposit status every 15s while still active. Stops on terminal.
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const data = await listMyCryptoDeposits(50);
        if (cancelled) return;
        const mine = (data.items || []).find((d) => d.id === deposit.deposit_id || d.payment_id === deposit.payment_id);
        if (mine) {
          setCurrentStatus(mine.status);
          setCredited(Boolean(mine.credited));
          setCreditedCents(Number(mine.credited_usd_cents || 0));
          setActuallyPaid(mine.actually_paid || null);
          if (mine.credited && TERMINAL_STATUSES.has(mine.status)) {
            // Final state — stop polling.
            return;
          }
        }
      } catch (_err) {
        // Soft-fail; next tick may succeed.
      }
      if (!cancelled) {
        timer = window.setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    timer = window.setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [deposit.deposit_id, deposit.payment_id]);

  const copy = async (text, field) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard');
      window.setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Couldn't copy — please select manually");
    }
  };

  const statusBadge = useMemo(() => {
    if (credited) {
      return {
        label: `Credited — ${formatUsd(creditedCents)}`,
        color: '#86efac',
        bg: 'rgba(56,189,127,0.15)',
        icon: CheckCircle2,
      };
    }
    if (TERMINAL_STATUSES.has(currentStatus)) {
      return {
        label: 'Confirmed — crediting…',
        color: '#FFC20E',
        bg: 'rgba(255,194,14,0.15)',
        icon: Loader2,
      };
    }
    if (ACTIVE_STATUSES.has(currentStatus)) {
      return {
        label: `Awaiting network — ${currentStatus}`,
        color: 'var(--sc-text-mute)',
        bg: 'rgba(255,255,255,0.05)',
        icon: Loader2,
      };
    }
    return {
      label: currentStatus || 'waiting',
      color: 'var(--sc-text-mute)',
      bg: 'rgba(255,255,255,0.05)',
      icon: Loader2,
    };
  }, [currentStatus, credited, creditedCents]);

  const StatusIcon = statusBadge.icon;
  const showSpinner = !credited;

  return (
    <div className="space-y-5" data-testid="crypto-deposit-screen-address">
      {/* Status badge */}
      <div
        className="rounded-xl px-4 py-3 flex items-center justify-between"
        style={{
          background: statusBadge.bg,
          border: `1px solid ${statusBadge.color === 'var(--sc-text-mute)' ? 'rgba(255,255,255,0.08)' : `${statusBadge.color}40`}`,
        }}
        data-testid="crypto-deposit-status-badge"
      >
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-4 h-4 ${showSpinner ? 'animate-spin' : ''}`} style={{ color: statusBadge.color }} />
          <div className="text-[12px] font-semibold uppercase tracking-[0.18em]" style={{ color: statusBadge.color }}>
            {statusBadge.label}
          </div>
        </div>
        {actuallyPaid && Number(actuallyPaid) > 0 ? (
          <div className="text-[11px] font-mono" style={{ color: 'var(--sc-text-dim)' }} data-testid="crypto-deposit-actually-paid">
            Received: {actuallyPaid} {deposit.pay_currency}
          </div>
        ) : null}
      </div>

      {/* Send-exactly card */}
      <div
        className="rounded-xl p-4 space-y-3"
        style={{
          background: 'rgba(0,0,0,0.32)',
          border: '1px solid rgba(255,194,14,0.22)',
        }}
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold mb-1" style={{ color: 'var(--sc-text-mute)' }}>
            Send exactly
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono font-bold text-lg" style={{ color: '#FFC20E' }} data-testid="crypto-deposit-pay-amount">
              {deposit.pay_amount} {deposit.pay_currency}
            </div>
            <button
              type="button"
              onClick={() => copy(`${deposit.pay_amount}`, 'amount')}
              data-testid="crypto-deposit-copy-amount-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition"
              style={{
                background: copiedField === 'amount' ? 'rgba(56,189,127,0.18)' : 'rgba(255,194,14,0.12)',
                border: copiedField === 'amount' ? '1px solid rgba(56,189,127,0.55)' : '1px solid rgba(255,194,14,0.4)',
                color: copiedField === 'amount' ? '#86efac' : '#FFC20E',
              }}
            >
              {copiedField === 'amount' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedField === 'amount' ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold mb-1" style={{ color: 'var(--sc-text-mute)' }}>
            To address
          </div>
          <div
            className="font-mono text-[11.5px] break-all leading-relaxed rounded-lg p-2.5"
            style={{
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.06)',
              color: 'var(--sc-text)',
            }}
            data-testid="crypto-deposit-pay-address"
          >
            {deposit.pay_address}
          </div>
          <button
            type="button"
            onClick={() => copy(deposit.pay_address, 'address')}
            data-testid="crypto-deposit-copy-address-btn"
            className="mt-2 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition"
            style={{
              background: copiedField === 'address' ? 'rgba(56,189,127,0.18)' : 'rgba(255,194,14,0.12)',
              border: copiedField === 'address' ? '1px solid rgba(56,189,127,0.55)' : '1px solid rgba(255,194,14,0.4)',
              color: copiedField === 'address' ? '#86efac' : '#FFC20E',
            }}
          >
            {copiedField === 'address' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copiedField === 'address' ? 'Copied' : 'Copy address'}
          </button>
        </div>

        <div
          className="text-[10.5px] pt-2 leading-relaxed"
          style={{
            color: 'var(--sc-text-dim)',
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          ~ {formatUsd(deposit.price_usd_cents)} estimated. Send from your wallet — your purchase balance updates automatically once the network confirms. Only
          the amount you actually send is credited.
        </div>
      </div>

      {/* New deposit button */}
      <button
        type="button"
        onClick={onReset}
        data-testid="crypto-deposit-new-btn"
        className="w-full rounded-xl py-2.5 font-semibold text-[12px] inline-flex items-center justify-center gap-2 transition"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: 'var(--sc-text-mute)',
        }}
      >
        <RefreshCcw className="w-3.5 h-3.5" />
        Create a new deposit
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Top-level panel.
// ---------------------------------------------------------------------------
const CryptoDepositPanel = () => {
  const [activeDeposit, setActiveDeposit] = useState(null);

  return (
    <section className="rounded-2xl p-5 sc-glass" style={{ border: '1px solid rgba(255,194,14,0.22)' }} data-testid="crypto-deposit-panel">
      <header className="flex items-center gap-3 mb-5">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(255,194,14,0.22), rgba(255,182,18,0.06))',
            border: '1px solid rgba(255,194,14,0.45)',
          }}
        >
          <Bitcoin className="w-4 h-4" style={{ color: '#FFC20E' }} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: '#FFC20E' }}>
            New · Recommended
          </div>
          <div className="font-display font-bold text-lg" style={{ color: 'var(--sc-text)' }}>
            Crypto Deposit
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: 'var(--sc-text-dim)' }}>
            One-time unique address per deposit. Funds auto-confirm on-chain.
          </div>
        </div>
      </header>

      {activeDeposit ? (
        <AddressDisplayScreen deposit={activeDeposit} onReset={() => setActiveDeposit(null)} />
      ) : (
        <AmountEntryScreen onAddressGenerated={setActiveDeposit} />
      )}
    </section>
  );
};

export default CryptoDepositPanel;
